/**
 * ProgressBar — top-fixed thin progress bar for the onboarding flow.
 *
 * 0..1 progress value, smooth width animation (300ms ease-out),
 * brand-purple fill on a 12%-opacity purple track. Optional step
 * counter label rendered underneath in right-aligned small text.
 *
 * Reference: tm-ai-workspace/resources/style-guide.md
 *  - Color: --color-primary #563f7a
 *  - Motion: 0.15–0.3s ease transitions, prefers-reduced-motion fallback
 */

import { useEffect, useRef } from 'react';
import {
  Animated,
  Easing,
  Platform,
  StyleSheet,
  View,
  type ViewProps,
} from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Brand, Spacing } from '@/constants/theme';

const TRACK_HEIGHT = 4;
const TRACK_BG = 'rgba(86, 63, 122, 0.12)';
const ANIM_DURATION_MS = 300;

type ProgressBarProps = {
  /** Value in [0, 1]. Clamped automatically. */
  progress: number;
  /** Optional right-aligned label, e.g. "2 / 6". */
  label?: string;
} & Omit<ViewProps, 'children'>;

export function ProgressBar({ progress, label, style, ...rest }: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(1, progress));
  const widthAnim = useRef(new Animated.Value(clamped)).current;

  useEffect(() => {
    const reduceMotion =
      Platform.OS === 'web' &&
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      widthAnim.setValue(clamped);
      return;
    }

    Animated.timing(widthAnim, {
      toValue: clamped,
      duration: ANIM_DURATION_MS,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [clamped, widthAnim]);

  const widthInterpolated = widthAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '100%'],
  });

  return (
    <View style={[styles.container, style]} {...rest}>
      <View style={styles.track} accessibilityRole="progressbar">
        <Animated.View style={[styles.fill, { width: widthInterpolated }]} />
      </View>
      {label ? (
        <ThemedText type="small" themeColor="textSecondary" style={styles.label}>
          {label}
        </ThemedText>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    gap: Spacing.two,
  },
  track: {
    height: TRACK_HEIGHT,
    width: '100%',
    backgroundColor: TRACK_BG,
    borderRadius: TRACK_HEIGHT / 2,
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    backgroundColor: Brand.primary,
    borderRadius: TRACK_HEIGHT / 2,
  },
  label: {
    textAlign: 'right',
    fontWeight: '500',
  },
});
